/**
 * BracketToolbar - Compact controls above the bracket view.
 *
 * Tufte-inspired: minimal chrome, maximum information density.
 * Segmented strategy control + fill button in one row.
 * Custom strategy reveals collapsible model weight sliders + upset targets.
 */

import { useState, useEffect } from 'react';

const STRATEGIES = [
  {
    value: 'chalk',
    label: 'Chalk',
    desc: 'Favors higher seeds and proven teams',
  },
  {
    value: 'balanced',
    label: 'Balanced',
    desc: 'Pure analytics — BPI, WAB, seed history, advanced stats',
  },
  {
    value: 'upset_hunter',
    label: 'Upsets',
    desc: 'Targets paper tigers and Cinderella runs',
  },
  {
    value: 'mascot_battle',
    label: 'Battle',
    desc: 'AI mascot fights — who would ACTUALLY win if the mascots battled?',
    premium: true,
  },
  {
    value: 'custom',
    label: 'Custom',
    desc: 'Tune model weights and upset targets',
    premium: true,
  },
];

const STRATEGY_VALUES = STRATEGIES.map(s => s.value);
const DEFAULT_WEIGHTS = { bpi: 25, wab: 20, seed_history: 20, three_pt: 0 };
const DEFAULT_UPSETS = { 1: 4, 2: 2 };

function BracketToolbar({
  bracket,
  onAutoFill,
  onShare,
  onExportImage,
  onReset,
  isAutoFilling = false,
  isExporting = false,
  readOnly = false,
  isPremium = false,
  onPremiumGate,
}) {
  const savedStrategy = bracket?.strategy || 'balanced';
  const initialStrategy = STRATEGY_VALUES.includes(savedStrategy) ? savedStrategy : 'balanced';
  const [selectedStrategy, setSelectedStrategy] = useState(initialStrategy);
  const [weights, setWeights] = useState(DEFAULT_WEIGHTS);
  const [upsetTargets, setUpsetTargets] = useState(DEFAULT_UPSETS);
  const [customExpanded, setCustomExpanded] = useState(true);

  // Sync strategy when bracket changes (e.g., loading a different bracket)
  useEffect(() => {
    if (bracket?.strategy && STRATEGY_VALUES.includes(bracket.strategy)) {
      setSelectedStrategy(bracket.strategy);
    }
  }, [bracket?.id, bracket?.strategy]);

  const totalPicks = bracket?.total_picks || 0;
  const isComplete = bracket?.is_complete === 1;
  const progress = Math.round((totalPicks / 63) * 100);
  const activeStrategy = STRATEGIES.find(s => s.value === selectedStrategy);

  const handleAutoFill = () => {
    if (selectedStrategy === 'custom') {
      onAutoFill?.('custom', { weights, upset_targets: upsetTargets });
    } else {
      onAutoFill?.(selectedStrategy);
    }
  };

  return (
    <div className="glass-card p-3 sm:p-4 space-y-3">
      {/* Row 1: Progress bar + actions */}
      <div className="flex items-center gap-3">
        {/* Progress */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="w-full max-w-[160px] h-1.5 bg-bg-primary rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-accent-blue to-success rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="text-[11px] font-medium text-text-secondary whitespace-nowrap">
            {totalPicks}/63
            {isComplete && <span className="text-success ml-1 font-bold">DONE</span>}
          </span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1.5 shrink-0">
          {!readOnly && totalPicks > 0 && (
            <>
              <button
                onClick={onExportImage}
                disabled={isExporting}
                className="p-1.5 text-text-secondary hover:text-accent-purple transition-colors disabled:opacity-50"
                title="Export bracket image"
              >
                {isExporting ? (
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                )}
              </button>
              <button
                onClick={onShare}
                className="p-1.5 text-text-secondary hover:text-accent-blue transition-colors"
                title="Share bracket"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
              </button>
              <button
                onClick={onReset}
                className="p-1.5 text-text-secondary hover:text-danger transition-colors"
                title="Reset bracket"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
                </svg>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Row 2: Prediction Models — strategy control + battle + fill */}
      {!readOnly && (
        <div className="space-y-2">
          {/* Label */}
          <span className="text-[10px] font-bold text-text-tertiary uppercase tracking-wider">
            Prediction Models
          </span>

          <div className="flex items-center gap-2">
            {/* Segmented control */}
            <div className="grid grid-cols-5 sm:flex bg-bg-primary/60 rounded-lg p-0.5 flex-1 min-w-0 gap-0.5 sm:gap-0">
              {STRATEGIES.map(s => {
                const isLocked = s.premium && !isPremium;
                return (
                  <button
                    key={s.value}
                    onClick={() => {
                      if (isLocked) {
                        onPremiumGate?.();
                        return;
                      }
                      setSelectedStrategy(s.value);
                    }}
                    className={`
                      px-2 sm:px-3 py-1.5 text-[11px] sm:text-xs font-semibold rounded-md transition-all duration-150 whitespace-nowrap sm:flex-1 flex items-center justify-center gap-1
                      ${selectedStrategy === s.value
                        ? 'bg-bg-card text-text-primary shadow-sm'
                        : isLocked
                          ? 'text-text-tertiary/50'
                          : 'text-text-secondary hover:text-text-primary'
                      }
                    `}
                  >
                    {s.label}
                    {isLocked && (
                      <svg className="w-2.5 h-2.5 text-yellow-500/70" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Fill — ghost button */}
            <button
              onClick={handleAutoFill}
              disabled={isAutoFilling}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-accent-blue/40 text-accent-blue hover:bg-accent-blue/10 transition-all disabled:opacity-50 shrink-0"
            >
              {isAutoFilling ? (
                <>
                  <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Filling...
                </>
              ) : (
                <>
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Fill
                </>
              )}
            </button>
          </div>

          {/* Strategy description */}
          <div className="text-[11px] text-text-secondary leading-snug pl-0.5">
            {activeStrategy?.desc}
            {selectedStrategy !== 'custom' && (
              <span className="text-text-tertiary"> — or click teams to pick manually</span>
            )}
          </div>

          {/* Custom strategy: collapsible panel */}
          {selectedStrategy === 'custom' && (
            <CustomStrategyPanel
              weights={weights}
              onWeightsChange={setWeights}
              upsetTargets={upsetTargets}
              onUpsetTargetsChange={setUpsetTargets}
              expanded={customExpanded}
              onToggle={() => setCustomExpanded(prev => !prev)}
            />
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Custom strategy panel with collapsible model weight sliders and upset targets.
 * Stacked vertically for mobile compatibility.
 */
function CustomStrategyPanel({ weights, onWeightsChange, upsetTargets, onUpsetTargetsChange, expanded, onToggle }) {
  const remainingWeight = 100 - weights.bpi - weights.wab - weights.seed_history - (weights.three_pt || 0);
  const totalUpsets = Object.values(upsetTargets).reduce((a, b) => a + b, 0);

  const handleWeight = (key, value) => {
    const maxAllowed = 100 - Object.entries(weights)
      .filter(([k]) => k !== key)
      .reduce((sum, [, v]) => sum + v, 0);
    onWeightsChange(prev => ({ ...prev, [key]: Math.min(value, maxAllowed) }));
  };

  const handleUpset = (round, value) => {
    onUpsetTargetsChange(prev => ({ ...prev, [round]: value }));
  };

  const WEIGHT_SLIDERS = [
    { key: 'bpi', label: 'BPI' },
    { key: 'wab', label: 'WAB' },
    { key: 'seed_history', label: 'Seed History' },
    { key: 'three_pt', label: '3PT Rate' },
  ];

  const UPSET_ROUNDS = [
    { round: 1, label: 'Round of 64', max: 16 },
    { round: 2, label: 'Round of 32', max: 8 },
  ];

  return (
    <div className="bg-bg-primary/40 rounded-lg overflow-hidden">
      {/* Collapse header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-bg-primary/60 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold text-text-primary uppercase tracking-wider">
            Custom Settings
          </span>
          <span className="text-[10px] text-text-tertiary">
            {remainingWeight}% to stats + momentum
          </span>
        </div>
        <svg
          className={`w-3.5 h-3.5 text-text-tertiary transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Collapsible body */}
      {expanded && (
        <div className="px-3 pb-3 space-y-3">
          {/* Model Weights */}
          <div className="space-y-2.5">
            <span className="text-[10px] font-semibold text-text-secondary uppercase tracking-wider">
              Model Weights
            </span>

            <div className="space-y-3">
              {WEIGHT_SLIDERS.map(({ key, label }) => (
                <div key={key} className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-medium text-text-secondary">{label}</span>
                    <span className="text-[11px] font-bold text-text-primary tabular-nums">
                      {weights[key]}%
                    </span>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={60}
                    step={5}
                    value={weights[key]}
                    onChange={(e) => handleWeight(key, parseInt(e.target.value))}
                    className="w-full slider-custom"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-border/20" />

          {/* Upset Targets — vertical stack for mobile */}
          <div className="space-y-2.5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-semibold text-text-secondary uppercase tracking-wider">
                Upset Targets
              </span>
              <span className="text-[10px] text-text-tertiary">
                {totalUpsets} total
              </span>
            </div>

            <div className="space-y-3">
              {UPSET_ROUNDS.map(({ round, label, max }) => (
                <div key={round} className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-medium text-text-secondary">{label}</span>
                    <span className="text-[11px] font-bold text-text-primary tabular-nums">
                      {upsetTargets[round] || 0}
                    </span>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={max}
                    value={upsetTargets[round] || 0}
                    onChange={(e) => handleUpset(round, parseInt(e.target.value))}
                    className="w-full slider-custom"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BracketToolbar;
